// src/services/prospectionServices.js
import axios from "axios";

const baseURL = `${process.env.REACT_APP_API_URL}/api/prospections`;

// Axios instance + token dynamique à chaque requête
const api = axios.create({ baseURL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Helpers
const buildQS = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    if (Array.isArray(v)) v.forEach((item) => q.append(k, item));
    else q.set(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : "";
};

const safe = async (p, fallback = null, label = "API") => {
  try {
    const { data } = await p;
    return data;
  } catch (err) {
    console.error(`${label} error:`, err);
    return fallback;
  }
};

/* ===================== GET (list, find, distinct, stats) ===================== */

// Liste paginée + filtrée
// params: { page, limit, search, discipline, departement, statut, site_status, last_email_status, sort }
export const getProspections = async (params = {}) =>
  safe(
    api.get(`${buildQS(params)}`),
    { data: [], pagination: { page: 1, limit: 0, total: 0, pages: 0 } },
    "getProspections",
  );

// Alias "all" (sans pagination spécifique, route alias côté serveur)
export const getAllProspections = async () =>
  safe(api.get(`/allProspections`), [], "getAllProspections");

// By ID
export const getProspectionById = async (id) =>
  safe(api.get(`/${id}`), null, "getProspectionById");

// find ciblé (ex: { email, nom_club })
export const findProspection = async (query = {}) =>
  safe(api.get(`/find${buildQS(query)}`), null, "findProspection");

// Distincts pour les filtres UI
export const getDistinctDisciplines = async () =>
  safe(api.get(`/distinct/disciplines`), [], "getDistinctDisciplines");

// ⬇️ maintenant accepte { q, limit }
export const getDistinctDepartements = async (params = {}) =>
  safe(
    api.get(`/distinct/departements${buildQS(params)}`),
    [],
    "getDistinctDepartements",
  );

// Stats
// KPIs globaux (totaux) selon les filtres courants
// params: { search, discipline, departement, statut, site_status, last_email_status, has_email, lic_min, lic_max }
export const getProspectionsKpis = async (params = {}) =>
  safe(
    api.get(`/stats/kpis${buildQS(params)}`),
    { total: 0, clients: 0, prospects: 0, interest: 0, desinscritsStatut: 0 },
    "getProspectionsKpis",
  );

export const getStatsSummary = async () =>
  safe(
    api.get(`/stats/summary`),
    { total: 0, parStatut: [], parDiscipline: [] },
    "getStatsSummary",
  );

export const getStatsHeatmapDepartements = async () =>
  safe(
    api.get(`/stats/heatmap-departements`),
    [],
    "getStatsHeatmapDepartements",
  );

// Export CSV (retourne un Blob)
export const exportProspectionsCsv = async (params = {}) =>
  safe(
    api.get(`/export/csv${buildQS(params)}`, { responseType: "blob" }),
    null,
    "exportProspectionsCsv",
  );

/* ===================== POST / PATCH / DELETE ===================== */

// Create
export const createProspection = async (payload) =>
  safe(api.post("", payload), null, "createProspection");

// Alias style "addProspection"
export const addProspection = async (payload) =>
  safe(api.post(`/addProspection`, payload), null, "addProspection");

// Update
export const updateProspection = async (id, payload) =>
  safe(api.patch(`/${id}`, payload), null, "updateProspection");

// Delete
export const deleteProspection = async (id) =>
  safe(api.delete(`/${id}`), null, "deleteProspection");

// Upsert (clé douce nom_club + email)
export const upsertProspection = async (payload) =>
  safe(api.post(`/upsert`, payload), null, "upsertProspection");

// Import en masse
export const bulkImportProspections = async (rows = [], mode = "insert") =>
  safe(
    api.post(`/bulk/import`, { rows, mode }),
    null,
    "bulkImportProspections",
  );

// Suppressions en masse
export const bulkDeleteProspections = async (ids = []) =>
  safe(api.delete(`/bulk`, { data: { ids } }), null, "bulkDeleteProspections");

// Statut rapide
export const updateProspectionStatus = async (
  id,
  { statut, date_contact, contacte_par } = {},
) =>
  safe(
    api.patch(`/${id}/status`, { statut, date_contact, contacte_par }),
    null,
    "updateProspectionStatus",
  );

// Marquer contacté
export const markProspectionContacted = async (
  id,
  { contacte_par, date_contact } = {},
) =>
  safe(
    api.patch(`/${id}/contacted`, { contacte_par, date_contact }),
    null,
    "markProspectionContacted",
  );

// Email tracking
export const markEmailEvent = async (
  payload /* { email, prospect_id?, event } */,
) => safe(api.post(`/email/mark-event`, payload), null, "markEmailEvent");

export const markUnsubscribe = async (payload /* { email | prospect_id } */) =>
  safe(api.post(`/email/mark-unsubscribe`, payload), null, "markUnsubscribe");

export const markBounce = async (payload /* { email | prospect_id } */) =>
  safe(api.post(`/email/mark-bounce`, payload), null, "markBounce");

/* ===================== ALIAS (compat avec tes anciens noms) ===================== */

// Ancien baseURL: /api/prospects ; on conserve les noms pour éviter de casser l’existant
export const getAllProspects = getAllProspections;
export const getProspectById = getProspectionById;
export const addProspect = addProspection;
export const updateProspect = updateProspection;
export const deleteProspect = deleteProspection;
